<template>
    <div id="VisitorMediaAndDocuments" class="p-6 bg-gray-50 rounded-lg shadow-md">
        <div v-if="loading" class="loading-spinner"></div>
        <div v-else>
            <div class="tab-menu mb-6">
                <div class="flex space-x-4">
                    <button
                        v-for="(tab, index) in tabs"
                        :key="index"
                        @click="handleTabClick(tab.id)"
                        :class="['tab-btn', activeTab === tab.id ? 'active' : '']">
                        <div class="flex items-center space-x-2">
                            <i :class="tab.icon" class="text-sm"></i>
                            <span class="text-base font-semibold">{{ tab.name }}</span>
                        </div>
                    </button>
                </div>
            </div>

            <div class="tab-content">
                <div v-if="activeTab === 'images'">
                    <div v-if="images.length === 0" class="empty-state">
                        <svg class="empty-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path
                                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" />
                        </svg>
                        <p class="text-gray-500">{{ $t('message.noMediaDocuments') }}</p>
                    </div>
                    <div v-else class="image-grid">
                        <button v-if="selectedImages.length > 0" @click="downloadSelectedImages"
                                class="download-button">{{ $t('message.download') }}
                        </button>
                        <div v-for="(image, index) in images" :key="index" class="image-item">
                            <viewer :images="images.map(image => image.url)">
                                <input type="checkbox" @change="toggleImageSelection(image.url)"
                                       :disabled="isSelectionLimitReached && !selectedImages.includes(image.url)">
                                <img :id="'img-' + index" :src="image.url" :alt="image.name" class="image">
                                <div class="image-overlay" @click="show(index)">
                                    <span class="image-name">{{ image.name }}</span>
                                </div>
                            </viewer>
                        </div>
                    </div>
                </div>

                <div v-if="activeTab === 'files'">
                    <div v-if="files.length === 0" class="empty-state">
                        <svg class="empty-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path
                                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" />
                        </svg>
                        <p class="text-gray-500">{{ $t('message.noMediaDocuments') }}</p>
                    </div>
                    <div v-else class="file-list space-y-3">
                        <button v-if="selectedFiles.length > 0" @click="downloadSelectedFiles" class="download-button">
                            {{ $t('message.download') }}
                        </button>
                        <div v-for="(file, index) in files" :key="index" class="file-item">
                            <input type="checkbox" @change="toggleFileSelection(file.url)"
                                   :disabled="isSelectionLimitReached && !selectedFiles.includes(file.url)">
                            <i class="fas fa-file-alt file-icon"></i>
                            <div class="file-info">
                                <p class="file-name">{{ file.name }}</p>
                                <p class="file-size">{{ file.size }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import Viewer from "v-viewer";
import "viewerjs/dist/viewer.css";
import Vue from "vue";

Vue.use(Viewer);

export default {
    name: "VisitorMediaAndDocuments",
    props: {
        visitorId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            activeTab: "images",
            loading: true,
            selectedImages: [],
            selectedFiles: [],
            maxSelection: 30,
            tabs: [
                { id: "images", name: this.$t('message.images'), icon: "fas fa-images" },
                { id: "files", name: this.$t('message.files'), icon: "fas fa-file-alt" },
                { id: "refresh", name: "Yenile", icon: "fas fa-sync" },
            ],
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            visitorMedia: (state) => state.visitor.visitorMedia,
        }),
        images() {
            return this.visitorMedia
                .filter((item) => item.mime.startsWith("image/"))
                .map((item) => ({
                    url: item.cdnUrl,
                    name: item.name,
                }));
        },
        files() {
            return this.visitorMedia
                .filter((item) => !item.mime.startsWith("image/"))
                .map((item) => ({
                    url: item.cdnUrl,
                    name: item.name,
                    size: (item.size / 1024).toFixed(2) + " KB",
                }));
        },
        isSelectionLimitReached() {
            if (this.selectedImages.length >= this.maxSelection || this.selectedFiles.length >= this.maxSelection) {
                this.$message.error(this.$t("message.selectionLimitError"));
                return true;
            }
            return false;
        },
    },
    methods: {
        ...mapActions({
            fetchVisitorMedia: "visitor/fetchVisitorMedia",
        }),
        async loadVisitorMedia() {
            try {
                await this.fetchVisitorMedia({
                    siteId: this.site._id,
                    visitorId: String(this.visitorId),
                });
            } catch (error) {
                console.error("Error loading visitor media:", error);
            } finally {
                this.loading = false;
            }
        },
        handleTabClick(tabId) {
            if (tabId === "refresh") {
                this.refreshMedia();
            } else {
                this.activeTab = tabId;
            }
        },
        async refreshMedia() {
            this.loading = true;
            await this.loadVisitorMedia();
        },
        openFile(url) {
            window.open(url, "_blank");
        },
        toggleImageSelection(url) {
            if (this.selectedImages.includes(url)) {
                this.selectedImages.splice(this.selectedImages.indexOf(url), 1);
            } else if (this.selectedImages.length < this.maxSelection) {
                this.selectedImages.push(url);
            } else {
                this.$message.error(this.$t("message.selectionLimitError"));
            }
        },
        toggleFileSelection(url) {
            if (this.selectedFiles.includes(url)) {
                this.selectedFiles.splice(this.selectedFiles.indexOf(url), 1);
            } else if (this.selectedFiles.length < this.maxSelection) {
                this.selectedFiles.push(url);
            } else {
                this.$message.error(this.$t("message.selectionLimitError"));
            }
        },
        downloadSelectedImages() {
            this.selectedImages.forEach((url) => {
                window.open(url, "_blank");
            });
        },
        downloadSelectedFiles() {
            this.selectedFiles.forEach((url) => {
                window.open(url, "_blank");
            });
        },
        show(index) {
            const id = "img-" + index;
            document.getElementById(id).click();
        },
    },
    created() {
        this.loadVisitorMedia();
    },
};
</script>

<style scoped>
#VisitorMediaAndDocuments {
    padding: 16px;

    .loading-spinner {
        border: 4px solid rgba(0, 0, 0, 0.1);
        border-left-color: #4f46e5;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        animation: spin 1s linear infinite;
        margin: 20px auto;
    }

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }

    .tab-menu {
        display: flex;
        justify-content: start;
        gap: 1rem;
        margin-bottom: 20px;

        .tab-btn {
            padding: 0.6rem 1.2rem;
            border: none;
            border-radius: 8px;
            background: none;
            color: #6b7280;
            transition: all 0.3s ease;
            font-size: 1rem;

            &:hover {
                background-color: #f3f4f6;
                color: #4f46e5;
            }

            &.active {
                background-color: #eef2ff;
                color: #4f46e5;
                font-weight: 600;
            }
        }
    }

    .image-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
        gap: 1rem;

        .image-item {
            position: relative;
            overflow: hidden;
            border-radius: 8px;
            transition: box-shadow 0.3s ease;
            cursor: pointer;

            &:hover {
                box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
            }

            input[type="checkbox"] {
                position: absolute;
                top: 8px;
                left: 8px;
                z-index: 10;
            }

            .image {
                width: 100%;
                height: 100px;
                object-fit: cover;
                border-radius: 8px;
            }

            .image-overlay {
                position: absolute;
                inset: 0;
                background: linear-gradient(to top, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.3), transparent);
                opacity: 0;
                transition: opacity 0.3s ease;
                display: flex;
                align-items: flex-end;
                padding: 8px;

                .image-item:hover & {
                    opacity: 1;
                }
            }

            .image-name {
                color: white;
                font-size: 0.875rem;
                font-weight: 500;
            }
        }
    }

    .file-item {
        display: flex;
        align-items: center;
        padding: 12px;
        background-color: #f9fafb;
        border-radius: 8px;
        transition: background-color 0.3s ease;
        cursor: pointer;

        &:hover {
            background-color: #eef2ff;
        }

        input[type="checkbox"] {
            margin-right: 12px;
        }

        .file-icon {
            font-size: 24px;
            color: #6b7280;
            margin-right: 12px;
        }

        .file-info {
            display: flex;
            flex-direction: column;
            justify-content: center;
            line-height: 1.2;

            .file-name {
                font-size: 1rem;
                font-weight: 600;
                color: #374151;
            }

            .file-size {
                font-size: 0.875rem;
                color: #6b7280;
            }
        }
    }

    .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        text-align: center;
        color: #6b7280;

        .empty-icon {
            width: 48px;
            height: 48px;
            margin-bottom: 16px;
            fill: #9ca3af;
        }
    }

    .download-button {
        padding: 0.6rem 1.2rem;
        background-color: #4f46e5;
        color: white;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        transition: background-color 0.3s ease;

        &:hover {
            background-color: #4338ca;
        }
    }
}
</style>
